import React, { useMemo } from "react";

import { useScheduleData, useViewData } from "../../store/magnetboardStore";
import { mapCoursePeriodSectionsSortedByQuarter } from "../../../../js/Mappers";
import { getCoursePeriodIdForPeriod } from "../../../../js/PeriodHelpers";
import StudentMagnetboardSection, {
  getStudentMainSectionsForCoursePeriod,
} from "./StudentMagnetboardSection";

const StudentMagnetboardRow = React.memo(function StudentMagnetboardRow({
  entity,
  data_index,
  magnetboardOptions,
  scheduleData: {
    scheduleStructureData,
    scheduleInfo,
    studentMatchPercentages,
    studentSectionsByStudentCoursePeriod,
    studentConflictSectionLookup,
    sectionsById,
    subsectionParentById,
    teachersById,
    coursesById,
    classroomsById,
    labelsById,
  },
  magnetboardActions: { toggleEditStudentSchedule, toggleEditSection, toggleEditData },
}) {
  const { periodsToShow, currentDay, periodType, currentViewType } = useViewData();
  const { scheduleType } = useScheduleData();

  const entity_id = entity.data_id;
  const entity_info = entity.display_info || {};
  const lunch_schedule_period_id = entity_info.lunch_schedule_period_id;
  const row_style = useMemo(
    () => ({ gridTemplateColumns: `170px repeat(${periodsToShow.length}, 1fr)` }),
    [periodsToShow.length]
  );

  const best_student_match = useMemo(() => {
    if (!studentMatchPercentages) return 0;

    const student_match_percent =
      entity_id in studentMatchPercentages
        ? studentMatchPercentages[entity_id].match_percent
        : 0;
    const student_percent_schedule_full =
      entity_id in studentMatchPercentages
        ? studentMatchPercentages[entity_id].percent_schedule_full
        : 0;

    return Math.round(Math.max(student_match_percent, student_percent_schedule_full));
  }, [entity_id, studentMatchPercentages]);

  const indicator_color =
    best_student_match === 100
      ? "green-stroke"
      : best_student_match >= 75 && best_student_match < 100
      ? "yellow-stroke"
      : "red-stroke";

  return (
    <div key={data_index} className="mb-schedule-row-parent">
      <div className="mb-row mb-schedule-row" style={row_style} data-ref={entity_id}>
        <div className="mb-schedule-main-inner-box">
          <div
            className="mb-schedule-row-name-container"
            style={{ display: "grid", gridTemplateColumns: "auto 1fr" }}
          >
            <svg viewBox="0 0 36 36" className={`mb-circular-chart ${indicator_color}`}>
              <path
                className="mb-circle-bg"
                d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="mb-circle"
                strokeDasharray={`${best_student_match}, 100`}
                d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <text x="18" y="20.35" className="mb-percentage">
                {best_student_match}%
              </text>
            </svg>
            <div>
              <div
                className={`mb-schedule-row-name ${magnetboardOptions.allow_hotlinks !== false ? 'mb-schedule-row-name-link' : ''}`}
                onClick={magnetboardOptions.allow_hotlinks !== false ? () => toggleEditData('student', entity_id) : null}
              >
                {`${entity_info.last_name || ""}, ${entity_info.first_name || ""}`}
              </div>
              <div className="mb-schedule-row-department dark-gray-text">
                {entity_info.grade}
              </div>
              <div
                className="blue-link small-text full-width"
                onClick={() => toggleEditStudentSchedule(entity_id)}
              >
                Edit Schedule
              </div>
            </div>
          </div>
        </div>

        {periodsToShow.map((period_info, index) => {
          const period_id = period_info.period_id;
          const course_period_id = getCoursePeriodIdForPeriod(scheduleStructureData, period_id, {
            period_type: periodType,
            schedule_type: scheduleType,
            current_day: currentDay,
          });

          if (!course_period_id) return null;

          const is_lunch_period =
            currentViewType === "day" && period_id === lunch_schedule_period_id;
          const main_sections = getStudentMainSectionsForCoursePeriod({
            studentId: entity_id,
            coursePeriodId: course_period_id,
            studentSectionsByStudentCoursePeriod,
            sectionsById,
            subsectionParentById,
          });
          const cell_sections = mapCoursePeriodSectionsSortedByQuarter([...main_sections]);

          return (
            <div
              className="mb-inner-box mb-schedule-inner-box"
              key={`${entity_id}-${course_period_id}-${index}`}
              style={{ position: "relative", minHeight: "60px" }}
            >
              {is_lunch_period && (
                <div className="mb-student-section">
                  <div className="mb-section-top-bar pastel-yellow">
                    <div>Lunch</div>
                  </div>
                  <div className="mb-section-content">
                    <div className="mb-section-name ellipsis">Lunch</div>
                  </div>
                </div>
              )}
              {cell_sections.map((section_info) => (
                <StudentMagnetboardSection
                  key={`${entity_id}-${course_period_id}-${section_info.section_id}`}
                  sectionInfo={section_info}
                  scheduleInfo={scheduleInfo}
                  scheduleType={scheduleType}
                  currentViewType={currentViewType}
                  currentDay={currentDay}
                  studentID={entity_id}
                  lockedSectionIds={entity_info.student_locked_sections || []}
                  sectionsById={sectionsById}
                  teachersById={teachersById}
                  coursesById={coursesById}
                  classroomsById={classroomsById}
                  labelsById={labelsById}
                  studentConflictSectionLookup={studentConflictSectionLookup}
                  magnetboardOptions={magnetboardOptions}
                  toggleEditSection={toggleEditSection}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default StudentMagnetboardRow;
